import updateSowingDate from '@dehaat/kisan-app-bl/api/updateSowingDate';
import Crop from '@dehaat/kisan-app-bl/models/CropV2';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';
import Paragraph, { VARIANT } from '@/components/Paragraph';
import SubHeading, { VARIANT as HEADING_VARIANT } from '@/components/SubHeading';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import SowingDate from './SowingDate';
interface Props {
  crop: Crop;
  onSubmit: (hasUsedMedicine: boolean) => void;
}
const AddSowingDate = ({
  crop: {
    app_display_name,
    id: cropId,
    image_url,
    is_perennial
  },
  onSubmit
}: Props) => {
  // Have 3 states (yes/no/not set) for medicine prompt since it's optional
  const [hasUsedMedicine, setHasUsedMedicine] = useState<boolean | null>(null);
  const [month, setMonth] = useState(NaN);
  const [week, setWeek] = useState(NaN);
  const [year, setYear] = useState(NaN);
  const {
    t
  } = useTranslation('askQuestion');
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const onPostQuestion = async () => {
    if (month) {
      await updateSowingDate({
        crop: cropId,
        time: new Date(year, month, (week || 0) * 7 + 1).toISOString()
      });
    }
    const eventParams = {
      Type: 'Disease',
      Sowing_Year: year,
      Sowing_Month: t(`sowingDate:month_${month}`),
      Sowing_Week: t(`sowingDate:week_${week}`),
      Used_Crop_Medicine: hasUsedMedicine ? t('common:yes') : t('common:yes')
    };
    trackCustomEvent(CustomEventTypes.ASK_HELP_QUESTION_POSTED, JSON.stringify(eventParams));
    onSubmit(Boolean(hasUsedMedicine));
  };
  return <>
      <div className="bg-white grow">
        <div className="border-b border-neutral-30 flex gap-2 items-center mt-4 p-4">
          <div className="border border-neutral-20 h-14 overflow-hidden relative rounded-full w-14">
            <Image alt={app_display_name} className="object-cover" fill src={image_url} data-sentry-element="Image" data-sentry-source-file="AddSowingDate.tsx" />
          </div>
          <Paragraph className="mt-1 text-neutral-80" isHighlighted variant={VARIANT.BIG} data-sentry-element="Paragraph" data-sentry-source-file="AddSowingDate.tsx">
            {app_display_name}
          </Paragraph>
        </div>
        <section className="p-4">
          <SubHeading className="mb-4" variant={HEADING_VARIANT.BIG} data-sentry-element="SubHeading" data-sentry-source-file="AddSowingDate.tsx">
            {`${t('sowing_date')} ${t('optional')}`}
          </SubHeading>
          <SowingDate isPerennial={is_perennial} month={month} onSetMonth={setMonth} onSetWeek={setWeek} onSetYear={setYear} week={week} year={year} data-sentry-element="SowingDate" data-sentry-source-file="AddSowingDate.tsx" />
        </section>
        <section className="border-neutral-10 border-t-[8px] p-4">
          <SubHeading className="mb-6" variant={HEADING_VARIANT.BIG} data-sentry-element="SubHeading" data-sentry-source-file="AddSowingDate.tsx">
          {`${t('medicine')} ${t('optional')}`}
          </SubHeading>
          <div>
            <button className={`border font-nato-medium mr-4 px-3 py-2 rounded-lg text-sm w-28 ${hasUsedMedicine ? 'bg-primary-10 border-primary-100' : 'border-neutral-20'}`} onClick={() => {
            trackCustomEvent(CustomEventTypes.ASK_HELP_CROP_MEDICINE_OPTION_SELECTED, JSON.stringify({
              Option_value: t('common:yes')
            }));
            setHasUsedMedicine(true);
          }}>
              {t('common:yes')}
            </button>
            <button className={`border font-nato-medium px-3 py-2 rounded-lg text-sm w-28 ${hasUsedMedicine === false ? 'bg-primary-10 border-primary-100' : 'border-neutral-20'}`} onClick={() => {
            trackCustomEvent(CustomEventTypes.ASK_HELP_CROP_MEDICINE_OPTION_SELECTED, JSON.stringify({
              Option_value: t('common:no')
            }));
            setHasUsedMedicine(false);
          }}>
              {t('common:no')}
            </button>
          </div>
        </section>
      </div>
      <div className="bg-white bottom-0 fixed m-auto max-w-7xl p-4 w-full">
        <button className="bg-primary-100 p-4 rounded-lg text-white w-full" onClick={onPostQuestion}>
          {t('post_question')}
        </button>
      </div>
    </>;
};
export default AddSowingDate;